import isEqual from 'lodash/isEqual'
import { distinctUntilChanged, takeWhile } from 'rxjs'

import PaperTool from '../paperTool/PaperTool'
import Zoom from '../zoom/Zoom.tool'
import { FLAG_COLORS, FLAG_TEXT_COLOR } from '../../../../../../constants/flagStyle'
import { FlagEnum } from '../../../../../../models/flags'
import { SNACKBAR_DURATION, SnackbarType } from '../../../../../../shared/components/snackbar/constants'
import { initial2DState } from '../../../../../slices/2D'
import { Coordinates2D, PaperToolConfig, Vector2D, VIEW_MODE } from '../../../../../types'
import addSelectFunctionalityToFlag from '../../../../utils/functionality-bindings/addSelectFunctionalityToFlag'

/**
 * Flag.tool.tsx
 * Creates a flag -- used for adding task and notes to page and materials
 */
export class Flag extends PaperTool {
    static NAME = 'FLAG'

    private static FLAG_SQUARE_SIZE_HEIGHT = 50
    // height of the triangle point at the bottom of the flag
    private static FLAG_POINT_HEIGHT = 20
    // size of the square when content is placeholder (#) or not more when content length is 2
    private static FLAG_SQUARE_SIZE_BASE_WIDTH = 50
    // space left when the content of the box is less than 3 lengths
    private static FLAG_CONTENT_SPACE_LEFT = 17
    // size of the square when content length is 3
    private static FLAG_SQUARE_SIZE_WIDTH_WITH_CONTENT = 62
    // space left when the content of the box is 3 lengths
    private static FLAG_CONTENT_SPACE_LEFT_WITH_CONTENT = 8
    private static FLAG_SQUARE_ARROW_Y_OFFSET = 5

    private currentZoom = initial2DState.currentZoom

    static INCREMENT = Zoom.INCREMENT
    static MIN_ITEM_SCALE = 0.2 // minimal item scale, change it if we need smaller items
    static MAX_ITEM_SCALE = 1 // normal item scale, should not be changed
    static MIN_FLAGS_ZOOM = 0.5 // when scale is less then 0.5, we do not scale more
    static MAX_FLAGS_ZOOM = 3 // when scale is more then 3, we do not scale more

    private cachedFlags: paper.Group[] = []
    private materialFlagOffset = 25
    private temporaryFlagGroups: paper.Group[] = []

    private disableFlagTool = false

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Flag.NAME

        this.mediator
            .get$()
            .pipe(
                takeWhile((state) => isEqual(state.common.activeMode, VIEW_MODE.Markup2D)),
                distinctUntilChanged(
                    (prev, curr) =>
                        prev['2D'].currentZoom === curr['2D'].currentZoom &&
                        prev['tools'].disableFlagTool === curr['tools'].disableFlagTool
                )
            )
            .subscribe((state) => {
                const nextZoom = state['2D'].currentZoom

                this.scaleFlags(nextZoom)
                this.disableFlagTool = state['tools'].disableFlagTool
            })
    }

    /**
     * Calculate the square size based on content
     * @param content
     */
    private calculateFlagSquareSize = (content: string): number => {
        if (content.length < 3) return Flag.FLAG_SQUARE_SIZE_BASE_WIDTH

        return Flag.FLAG_SQUARE_SIZE_WIDTH_WITH_CONTENT
    }

    /**
     * Calculate the content left space base on content
     * @param content
     */
    private calculateFlagContentLeftSpace = (content: string): number => {
        if (content.length === 1) return Flag.FLAG_CONTENT_SPACE_LEFT

        return Flag.FLAG_CONTENT_SPACE_LEFT_WITH_CONTENT
    }

    private getFlagColor = (flagType: string | null): string => {
        switch (flagType) {
            case FlagEnum.NOTE:
                return FLAG_COLORS[FlagEnum.NOTE].color
            case FlagEnum.CFB:
                return FLAG_COLORS[FlagEnum.CFB].color

            case FlagEnum.TASK:
            default:
                return FLAG_COLORS[FlagEnum.TASK].color
        }
    }

    private getFlagPositionFromCoordinates = ([x, y]: [number, number]) => {
        const currentScale = this.getCurrentFlagsScaling()

        // shift the y coordinate so the tip of the flag points to the exact coordinates
        const totalFlagHeight = Flag.FLAG_SQUARE_SIZE_HEIGHT + Flag.FLAG_POINT_HEIGHT
        const flagYCoordinates = y - (totalFlagHeight / 2) * currentScale

        return { x, y: flagYCoordinates }
    }

    /**
     * Draws a flat-top marker badge shape matching the design.
     */
    private drawFlagBadgeShape = (x: number, y: number, flagSize: number) => {
        const height = Flag.FLAG_SQUARE_SIZE_HEIGHT
        const width = flagSize
        const pointHeight = 20
        const topCornerRadius = 8

        const topY = y - height
        const bottomY = y

        const path = new this.paper.Path()

        path.moveTo(new this.paper.Point(x - width / 2, topY + topCornerRadius))

        path.quadraticCurveTo(
            new this.paper.Point(x - width / 2, topY),
            new this.paper.Point(x - width / 2 + topCornerRadius, topY)
        )

        path.lineTo(new this.paper.Point(x + width / 2 - topCornerRadius, topY))

        path.quadraticCurveTo(
            new this.paper.Point(x + width / 2, topY),
            new this.paper.Point(x + width / 2, topY + topCornerRadius)
        )

        path.lineTo(new this.paper.Point(x + width / 2, bottomY))

        path.lineTo(new this.paper.Point(x + 4, y + pointHeight - 2))

        path.quadraticCurveTo(
            new this.paper.Point(x, y + pointHeight),
            new this.paper.Point(x - 4, y + pointHeight - 2)
        )

        path.lineTo(new this.paper.Point(x - width / 2, bottomY))

        path.lineTo(new this.paper.Point(x - width / 2, topY + topCornerRadius))

        path.closePath()

        return path
    }

    /**
     * Draws a flag.
     */
    private drawFlag = (
        coords: [number, number],
        flagContent = '#',
        flagColor: string = FLAG_COLORS[FlagEnum.TASK].color,
        flagSize = Flag.FLAG_SQUARE_SIZE_BASE_WIDTH,
        isTemporaryFlag = false
    ) => {
        const { x, y } = this.getFlagPositionFromCoordinates(coords)
        const style = isTemporaryFlag
            ? FLAG_COLORS.TEMPORARY
            : { fill: flagColor, textColor: FLAG_TEXT_COLOR, stroke: null }

        const badgeShape = this.drawFlagBadgeShape(x, y, flagSize)

        badgeShape.fillColor = new this.paper.Color(style.fill)
        badgeShape.strokeColor = style.stroke ? new this.paper.Color(style.stroke) : null
        badgeShape.strokeWidth = style.stroke ? 2 : 0
        badgeShape.data.selectable = false

        const flagPointText = new this.paper.PointText({
            point: new this.paper.Point(x, y - 10),
            content: flagContent,
            fontSize: 25,
            fillColor: style.textColor,
            fontWeight: 'bold',
            justification: 'center',
            locked: true,
        })

        flagPointText.bringToFront()

        const flagGroup = new this.paper.Group([badgeShape, flagPointText])

        addSelectFunctionalityToFlag(flagGroup)
        flagGroup.scale(this.getCurrentFlagsScaling())

        flagGroup.position = new this.paper.Point(x, y)

        return flagGroup
    }

    private drawFlagOnClick = (event: paper.ToolEvent) => {
        const x = event.point.x
        const y = event.point.y

        const hit = this.getPaperScope().project.hitTest(event.point)

        const flagGroup = this.drawFlag(
            [x, y],
            '#',
            FLAG_COLORS[FlagEnum.TASK].color,
            Flag.FLAG_SQUARE_SIZE_BASE_WIDTH,
            true
        )

        const documentChunk = this.getActiveDocumentChunk()

        flagGroup.data.toolName = this.name
        flagGroup.data.isTemporaryGroup = true
        flagGroup.data.opening_id = hit?.item?.data?.drawable_id || null
        flagGroup.data.document_chunk_id = documentChunk?.id || null

        return flagGroup
    }

    drawFlagByCoords = (
        coords: [number, number],
        flag_id: number,
        openingId: number | null,
        order: number,
        type: string | null,
        isFlagVisible: boolean,
        status?: number | null,
        jiraStatusId?: number | null,
        isTemporaryFlag = false,
        linkId?: number,
        categoryId?: number | null,
        createdByEmail?: string | null
    ) => {
        const flagContent = order.toString()

        const flagSize = this.calculateFlagSquareSize(flagContent)

        const flagColor = this.getFlagColor(type)

        const flagGroup = this.drawFlag(coords, flagContent, flagColor, flagSize, isTemporaryFlag)

        flagGroup.data.toolName = this.name
        flagGroup.data.flag_id = flag_id
        flagGroup.data.isTemporaryGroup = isTemporaryFlag
        flagGroup.data.openingId = openingId
        flagGroup.data.id = linkId
        flagGroup.data.type = type
        flagGroup.data.status = status
        flagGroup.data.jiraStatusId = jiraStatusId
        flagGroup.data.categoryId = categoryId
        flagGroup.data.createdByEmail = createdByEmail
        flagGroup.visible = isFlagVisible

        this.addCachedFlags([flagGroup])

        return flagGroup
    }

    getFlagPointFromCoordinates = (coords: Coordinates2D, isFlagCentered: boolean, flagIndex?: number): Vector2D => {
        let centerPointX: number
        let centerPointY: number

        const adjustedCoordinatesByIndex = flagIndex ? flagIndex * this.materialFlagOffset : 0

        // center point for a point
        if (coords.length === 1) {
            const [x, y] = coords[0]

            return [x, y - adjustedCoordinatesByIndex]
        }

        // center point for area
        if (isFlagCentered) {
            ;[centerPointX, centerPointY] = coords.reduce(([sumX, sumY], [x, y]) => [sumX + x, sumY + y], [0, 0])

            const adjustedCenterPointY = centerPointY / coords.length

            centerPointX = centerPointX / coords.length

            return [centerPointX + adjustedCoordinatesByIndex, adjustedCenterPointY - adjustedCoordinatesByIndex]
        }

        // general calculation of the point
        const middleItemNumber = Math.floor(coords.length / 2) - 1

        centerPointX = (coords[middleItemNumber][0] + coords[middleItemNumber + 1][0]) / 2
        centerPointY = (coords[middleItemNumber][1] + coords[middleItemNumber + 1][1]) / 2

        return [centerPointX + adjustedCoordinatesByIndex, centerPointY - adjustedCoordinatesByIndex]
    }

    scaleFlags(nextZoom: number) {
        if (this.cachedFlags.length === 0) return

        if (this.currentZoom === nextZoom) return

        if (nextZoom <= Flag.MIN_FLAGS_ZOOM || nextZoom >= Flag.MAX_FLAGS_ZOOM) {
            return
        }

        // calculate next scale step based on difference between current and next zoom
        const nextStep = Number(Math.abs(this.currentZoom / nextZoom).toFixed(1))

        const currentScale = this.getCurrentFlagsScaling()
        const potentialScale = currentScale * nextStep

        this.cachedFlags.forEach((item) => {
            const itemBottomCenter = item.bounds.bottomCenter

            // Enforce min and max scale limits
            if (potentialScale > Flag.MAX_ITEM_SCALE) {
                // Cap the scale to the max scale
                item.scale(Flag.MAX_ITEM_SCALE / currentScale)
            } else if (potentialScale < Flag.MIN_ITEM_SCALE) {
                // Cap the scale to the min scale
                item.scale(Flag.MIN_ITEM_SCALE / currentScale)
            } else {
                // Scale normally if within the allowed range
                item.scale(nextStep)
            }

            item.bounds.bottomCenter = itemBottomCenter
        })

        this.currentZoom = nextZoom
    }

    getCachedFlags = () => {
        return this.cachedFlags
    }

    addCachedFlags = (flags: paper.Group[]) => {
        flags.forEach((flag) => {
            const flagId = flag.data.flag_id
            const linkId = flag.data.id

            const exists = this.cachedFlags.some(
                (cachedFlag) => cachedFlag.data.flag_id === flagId && cachedFlag.data.id === linkId
            )

            if (!exists) {
                this.cachedFlags.push(flag)
            }
        })
    }

    removeCachedFlagsByDataFlagId = (flagIds: number[]) => {
        this.cachedFlags = this.cachedFlags.filter((cachedFlag) => !flagIds.includes(cachedFlag.data.flag_id))
    }

    removeCachedFlagByFlagIdAndLinkId = (flagId: number, openingLinkId: number) => {
        this.cachedFlags = this.cachedFlags.filter(
            (cachedFlag) => !(cachedFlag.data.flag_id === flagId && cachedFlag.data.id === openingLinkId)
        )
    }

    removeFlagByFlagIdAndLinkId = (flagId: number, openingLinkId: number) => {
        const flagsToRemove = this.cachedFlags.filter(
            (flag) => flag.data.flag_id === flagId && flag.data.id === openingLinkId
        )

        flagsToRemove.forEach((flag) => flag.remove())

        this.removeCachedFlagByFlagIdAndLinkId(flagId, openingLinkId)
    }

    resetCachedFlags = () => {
        this.cachedFlags = []
    }

    getCurrentFlagsScaling = () => {
        if (this.cachedFlags.length > 0) {
            return Math.min(this.cachedFlags[0].children[1].scaling.x, this.cachedFlags[0].children[1].scaling.y)
        }

        return 1
    }

    onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event) || this.disableFlagTool) return

        this.temporaryFlagGroups.push(this.drawFlagOnClick(event))

        this.setState('materialFlags', { isOpenFlagForm: true })
    }

    cancel = () => {
        this.temporaryFlagGroups.forEach((group) => {
            group.remove()
        })

        this.temporaryFlagGroups = []
        this.setState('materialFlags', { isOpenFlagForm: false })
    }

    onMouseDrag = (event) => {
        this.toolPanning(event)
    }

    onMouseUp = (event: paper.ToolEvent) => {
        if (!this.isPanningClick(event) && this.disableFlagTool) {
            this.setState('common', {
                snackbarData: {
                    body: 'Close the current flag before creating a new one.',
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                    },
                    type: SnackbarType.INFO,
                    autoHideDuration: SNACKBAR_DURATION.LONG,
                },
            })
        }
    }

    getFlagCoordinates = (flagGroup: paper.Item) => {
        const flagSquare: paper.Path.Rectangle = flagGroup.children[0] as paper.Path.RegularPolygon

        return [flagSquare.position.x, flagSquare.position.y + Flag.FLAG_SQUARE_ARROW_Y_OFFSET]
    }
}

export default Flag
